import { Button, Input, Spin, Tag, message as antdMessage } from "antd";
import { CheckOutlined, CloseOutlined, EditOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import type { StreamState } from "../hooks/useStreamingDraft";
import { approveDraft, discardDraft, editDraft } from "../api/ai";

type Props = {
  state: StreamState;
  onSent?: () => void;
  onDismissed?: () => void;
};

export function AiDraftPreview({ state, onSent, onDismissed }: Props) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    if (state.status === "ready") {
      setEditValue(state.text);
      setEditing(false);
    }
  }, [state]);

  if (state.status === "idle") return null;

  const isStreaming = state.status === "streaming" || state.status === "starting";
  const isReady = state.status === "ready";
  const isError = state.status === "error";
  const text =
    state.status === "streaming" || state.status === "ready"
      ? state.text
      : "";
  const draftId = state.status === "streaming" || state.status === "ready" ? state.draftId : null;

  const onApprove = async () => {
    if (!draftId) return;
    setApproving(true);
    try {
      if (editing) {
        await editDraft(draftId, editValue);
      }
      await approveDraft(draftId);
      antdMessage.success("已发送");
      onSent?.();
    } catch {
      antdMessage.error("发送失败");
    } finally {
      setApproving(false);
    }
  };

  const onSaveEdit = async () => {
    if (!draftId) return;
    setSaving(true);
    try {
      await editDraft(draftId, editValue);
      setEditing(false);
      antdMessage.success("已保存");
    } catch {
      antdMessage.error("保存失败");
    } finally {
      setSaving(false);
    }
  };

  const onDiscard = async () => {
    if (draftId) {
      await discardDraft(draftId).catch(() => null);
    }
    onDismissed?.();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
      style={{
        border: "1px solid var(--ant-color-primary)",
        borderRadius: 12,
        padding: 16,
        background:
          "color-mix(in srgb, var(--ant-color-primary) 6%, var(--ant-color-bg-container))",
        margin: "0 0 12px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Tag color="purple" style={{ marginInlineEnd: 0 }}>
          <ThunderboltOutlined /> AI 起草
        </Tag>
        {isStreaming ? (
          <span style={{ fontSize: 14, color: "var(--ant-color-text-secondary)" }}>
            <Spin size="small" /> 起草中…
          </span>
        ) : null}
        {isReady ? (
          <span style={{ fontSize: 14, color: "var(--ant-color-text-secondary)" }}>已就绪</span>
        ) : null}
        {isError ? <Tag color="error">生成失败</Tag> : null}
      </div>

      {isStreaming ? (
        <div
          style={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontSize: 16,
            lineHeight: 1.6,
            minHeight: 60,
          }}
        >
          {text}
          <span className="caret" aria-hidden style={{ opacity: 0.6, marginLeft: 1 }}>▍</span>
        </div>
      ) : null}

      {isReady && !editing ? (
        <div
          style={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontSize: 16,
            lineHeight: 1.6,
          }}
        >
          {text || (
            <span style={{ color: "var(--ant-color-text-secondary)" }}>(模型未生成有效内容)</span>
          )}
        </div>
      ) : null}

      {isReady && editing ? (
        <Input.TextArea
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          autoSize={{ minRows: 4, maxRows: 12 }}
          style={{ borderRadius: 10, fontSize: 16 }}
        />
      ) : null}

      {isError ? (
        <div style={{ fontSize: 15, color: "var(--ant-color-error)" }}>{state.message}</div>
      ) : null}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
        {isReady ? (
          editing ? (
            <>
              <Button onClick={() => setEditing(false)}>取消编辑</Button>
              <Button type="primary" loading={saving} onClick={onSaveEdit}>
                保存
              </Button>
            </>
          ) : (
            <>
              <Button icon={<CloseOutlined />} onClick={onDiscard}>
                丢弃
              </Button>
              <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>
                编辑
              </Button>
              <Button
                type="primary"
                icon={<CheckOutlined />}
                loading={approving}
                disabled={!editValue.trim()}
                onClick={onApprove}
              >
                批准并发送
              </Button>
            </>
          )
        ) : null}
        {isError ? (
          <Button onClick={onDismissed}>关闭</Button>
        ) : null}
      </div>
    </motion.div>
  );
}
